import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export default function ManageBooking() {
  const { token } = useParams<{ token: string }>();
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showSlots, setShowSlots] = useState(false);
  const [slotsByDay, setSlotsByDay] = useState<Record<string, any[]>>({});
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/booking/${token}`)
      .then(res => res.json())
      .then(data => {
        setBooking(data.data || data);
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function cancel() {
    const res = await fetch(`${import.meta.env.VITE_API_URL}/cancel-booking/${token}`, {
      method: "POST",
    });
    const data = await res.json();
    setMessage(data.message);
    setBooking((prev: any) => ({ ...prev, booking: "cancelled" }));
  }

  async function loadSlots() {
    if (!booking?.praticien_id) {
      setMessage("Unable to load slots: missing practitioner ID");
      return;
    }
    setShowSlots(true);
    setMessage("Loading available slots…");
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/disponibilites/${booking.praticien_id}`
      );
      const data = await res.json();

      // group slots by day_iso
      const grouped: Record<string, any[]> = {};
      (data || []).forEach((slot: any) => {
        if (!grouped[slot.day_iso]) grouped[slot.day_iso] = [];
        grouped[slot.day_iso].push(slot);
      });
      setSlotsByDay(grouped);
      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Error while loading slots.");
    }
  }

  async function chooseSlot(day_iso: string, time_hhmm: string) {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/reschedule-booking/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ day_iso, time_hhmm }),
      });
      const data = await res.json();
      setMessage(data.message || "Appointment successfully rescheduled!");
      setShowSlots(false);
      setBooking((prev: any) => ({
        ...prev,
        day_iso,
        time_hhmm,
        booking: "booked",
      }));
    } catch (err) {
      console.error(err);
      setMessage("Error while rescheduling appointment.");
    }
  }

  function formatDate(day_iso: string) {
    const date = new Date(day_iso);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric"
    });
  }

  if (loading) return <p>Loading…</p>;
  if (!booking) return <p>No booking found.</p>;

  return (
    <div className="max-w-lg mx-auto p-6 bg-white rounded shadow">
      <h1 className="text-xl font-bold mb-4">Appointment Management</h1>
      <p>Date: {booking.day_iso}</p>
      <p>Time: {booking.time_hhmm}</p>
      <p>Status: {booking.booking}</p>

      {booking.booking === "cancelled" ? (
        <p className="mt-4 text-red-600">
          Please contact the clinic to book a new appointment.
        </p>
      ) : (
        <div className="space-y-3 mt-4">
          <button
            onClick={cancel}
            className="w-full bg-red-600 text-white py-2 rounded"
          >
            Cancel
          </button>
          <button
            onClick={loadSlots}
            className="w-full bg-yellow-500 text-white py-2 rounded"
          >
            Reschedule
          </button>
        </div>
      )}

      {showSlots && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Choose a new day</h2>
          {Object.keys(slotsByDay).length === 0 && <p>No slots available.</p>}
          <ul className="space-y-4">
            {Object.keys(slotsByDay).map((day) => (
              <li key={day} className="border rounded p-3">
                <button
                  onClick={() =>
                    setExpandedDay(expandedDay === day ? null : day)
                  }
                  className="w-full text-left font-semibold text-blue-600"
                >
                  {formatDate(day)}
                </button>
                {expandedDay === day && (
                  <ul className="mt-2 space-y-2">
                    {slotsByDay[day].map((slot, i) => (
                      <li key={i}>
                        <button
                          onClick={() => chooseSlot(slot.day_iso, slot.time_hhmm)}
                          className="w-full bg-blue-500 text-white py-2 rounded"
                        >
                          {slot.time_hhmm}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {message && <p className="mt-4 text-blue-600">{message}</p>}
    </div>
  );
}
